/**
 * Authentication Storage Utilities
 * Handles token storage with support for both Firebase and Legacy authentication
 */

import { TOKENS } from '../constants';
import { deleteCookie, getCookie } from './cookie';

/**
 * Set authentication token in localStorage
 * @param {string} key - Token key (ACCESS, REFRESH, AUTH_TYPE)
 * @param {string} value - Token value
 */
export const setAuthToken = (key, value) => {
  try {
    if (value) {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error('Error setting auth token:', error);
  }
};

/**
 * Get authentication token from localStorage
 * @param {string} key - Token key (ACCESS, REFRESH, AUTH_TYPE)
 * @returns {string|null} Token value or null if not found
 */
export const getAuthToken = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

/**
 * Clear all authentication tokens
 */
export const clearAllAuthTokens = () => {
  try {
    localStorage.removeItem(TOKENS.ACCESS);
    localStorage.removeItem(TOKENS.REFRESH);
    localStorage.removeItem(TOKENS.AUTH_TYPE);
  } catch (error) {
    console.error('Error clearing auth tokens:', error);
  }
};

/**
 * Get current authentication type
 * @returns {string|null} 'firebase', 'legacy', or null
 */
export const getAuthType = () => {
  return getAuthToken(TOKENS.AUTH_TYPE);
};

/**
 * Check if user is authenticated (has valid access token)
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  const accessToken = getCookie(TOKENS.ACCESS) || getAuthToken(TOKENS.ACCESS);
  return !!accessToken;
};

/**
 * Check if access token is expired
 * Decodes JWT token and compares exp timestamp with current time
 * @returns {boolean} True if token is expired or invalid, false if valid
 */
export const isAccessTokenExpired = () => {
  try {
    const token = getCookie(TOKENS.ACCESS) || getAuthToken(TOKENS.ACCESS);
    if (!token) {
      // No token means it's expired/invalid
      return true;
    }

    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      // Invalid token format
      return true;
    }

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    if (!payload.exp) {
      // No expiration claim means it's invalid
      return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Math.floor(Date.now() / 1000);

    // Add 60 second buffer to refresh before actual expiration
    return payload.exp < currentTime + 60;
  } catch (error) {
    // If we can't decode the token, consider it expired
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get current refresh token
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  return getAuthToken(TOKENS.REFRESH);
};

/**
 * Update tokens after refresh (maintains auth_type)
 * @param {Object} refreshData - Token refresh response
 * @param {string} refreshData.access_token - New access token
 * @param {string} refreshData.refresh_token - New refresh token (optional for legacy)
 * @param {string} refreshData.auth_type - Authentication type
 */
export const updateTokensAfterRefresh = (refreshData) => {
  try {
    if (refreshData.access_token) {
      setAuthToken(TOKENS.ACCESS, refreshData.access_token);
    }
    if (refreshData.refresh_token) {
      setAuthToken(TOKENS.REFRESH, refreshData.refresh_token);
    }
    if (refreshData.auth_type) {
      setAuthToken(TOKENS.AUTH_TYPE, refreshData.auth_type);
    }
  } catch (error) {
    console.error('Error updating tokens after refresh:', error);
  }
};

/**
 * Comprehensive logout function that clears all user data
 * @param {boolean} showMessage - Whether to show a logout message
 * @param {string} message - Custom message to display
 */
export const performCompleteLogout = (
  showMessage = false,
  message = 'Session expired. Please log in again.'
) => {
  try {
    // 1. Clear all localStorage tokens and data
    clearAllAuthTokens();

    // 2. Clear additional localStorage items including Redux Persist
    try {
      localStorage.removeItem('user_data');
      localStorage.removeItem('permissions');
      localStorage.removeItem('menu');
      localStorage.removeItem('profile_data');
      localStorage.removeItem('client_data');
      localStorage.removeItem('agency_data');

      // Clear Redux Persist storage (critical for preventing old user data)
      localStorage.removeItem('persist:root');

      // Clear any other potential Redux Persist keys
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('persist:')) {
          localStorage.removeItem(key);
        }
      });
    } catch (localStorageError) {
      console.error('Error clearing additional localStorage items:', localStorageError);
    }

    // 3. Clear all cookies
    try {
      deleteCookie(TOKENS.ACCESS);
      deleteCookie(TOKENS.REFRESH);
      deleteCookie(TOKENS.AUTH_TYPE);
    } catch (cookieError) {
      console.error('Error clearing cookies:', cookieError);
    }

    // 4. Clear sessionStorage (except for logout message if needed)
    try {
      if (showMessage) {
        sessionStorage.setItem('force_relogin_message', message);
      }
      // Clear other sessionStorage items but preserve the message
      const messageToPreserve = sessionStorage.getItem('force_relogin_message');
      sessionStorage.clear();
      if (messageToPreserve && showMessage) {
        sessionStorage.setItem('force_relogin_message', messageToPreserve);
      }
    } catch (sessionStorageError) {
      console.error('Error clearing sessionStorage:', sessionStorageError);
    }
  } catch (error) {
    console.error('Error during complete logout:', error);
  }
};
